import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ThermometerSun, Calendar, Image, CheckCircle, Trash2, Loader2, Search, Plus } from 'lucide-react'
import { toast } from 'sonner'
import { type ColumnDef } from '@tanstack/react-table'
import { PageHeader } from '@/components/layout/page-header'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { DataTable } from '@/components/ui/data-table'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog'
import { Combobox } from '@/components/ui/combobox'
import { useSickReports, useSickReportStats } from '@/hooks/useSickReports'
import { useUsers } from '@/hooks/useUsers'
import { sickReports as sickApi } from '@/utils/api'
import { formatDate, getStatusLabel } from '@/utils/formatters'
import { cn } from '@/lib/utils'

interface UserOption { id: number; name: string }

function AddSickForm({ users, onClose, onSuccess }: { users: UserOption[]; onClose: () => void; onSuccess: () => void }) {
  const [formData, setFormData] = useState({ user_id: '', start_date: '', end_date: '', notes: '' })
  const [proofFile, setProofFile] = useState<File | null>(null)
  const [saving, setSaving] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.user_id || !formData.start_date) { toast.error('Mitarbeiter und Startdatum sind erforderlich'); return }
    if (formData.end_date && formData.end_date < formData.start_date) { toast.error('Enddatum darf nicht vor dem Startdatum liegen'); return }
    setSaving(true)
    try {
      await sickApi.create({
        user_id: parseInt(formData.user_id),
        start_date: formData.start_date,
        end_date: formData.end_date || undefined,
        notes: formData.notes || undefined,
        proofPhoto: proofFile,
      })
      onSuccess()
    } catch (err: any) {
      toast.error(err?.response?.data?.error || (err instanceof Error ? err.message : 'Fehler'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <label className="text-sm font-medium">Mitarbeiter</label>
        <Combobox
          options={users.map(u => ({ value: String(u.id), label: u.name }))}
          value={formData.user_id}
          onChange={(v) => setFormData({ ...formData, user_id: v })}
          placeholder="Auswählen..."
          searchPlaceholder="Mitarbeiter suchen..."
          emptyText="Kein Mitarbeiter gefunden"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <label className="text-sm font-medium">Von</label>
          <Input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} required />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Bis <span className="text-xs text-muted-foreground">(optional)</span></label>
          <Input type="date" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} />
        </div>
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium">Bemerkung <span className="text-xs text-muted-foreground">(optional)</span></label>
        <Input value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="z.B. AU-Bescheinigung wird nachgereicht" />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium">AU-Bescheinigung <span className="text-xs text-muted-foreground">(optional, JPG/PNG/PDF)</span></label>
        <Input
          type="file"
          accept=".jpg,.jpeg,.png,.pdf"
          onChange={(e) => setProofFile(e.target.files?.[0] || null)}
        />
        {proofFile && <p className="text-xs text-muted-foreground">{proofFile.name} ({Math.round(proofFile.size / 1024)} KB)</p>}
      </div>
      <div className="flex justify-end gap-3 pt-4">
        <Button variant="outline" type="button" onClick={onClose}>Abbrechen</Button>
        <Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Eintragen</Button>
      </div>
    </form>
  )
}

interface SickReport {
  id: number
  user_name: string
  project_name: string | null
  start_date: string
  end_date: string | null
  days_count: number | null
  actual_sick_days: number | null
  status: string
  photo_url: string | null
  no_proof_reason: string | null
}

interface SickStats {
  activeCount: number
  monthlyDays: number
  yearlyDays: number
}

export default function Krankmeldungen() {
  const queryClient = useQueryClient()
  const [statusFilter, setStatusFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [endModal, setEndModal] = useState<{ open: boolean; report: SickReport | null }>({ open: false, report: null })
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; report: SickReport | null }>({ open: false, report: null })
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0])
  const [photoModal, setPhotoModal] = useState<{ open: boolean; url: string | null }>({ open: false, url: null })
  const [addModal, setAddModal] = useState(false)
  const { data: users = [] } = useUsers()

  const { data: reports = [], isLoading } = useSickReports(
    statusFilter === 'all' ? undefined : { status: statusFilter }
  )
  const { data: stats } = useSickReportStats({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  })

  const endMutation = useMutation({
    mutationFn: ({ id, endDate }: { id: number; endDate: string }) =>
      sickApi.update(id, { end_date: endDate, status: 'ended' }),
    onSuccess: () => {
      toast.success('Krankmeldung beendet')
      setEndModal({ open: false, report: null })
      queryClient.invalidateQueries({ queryKey: ['sick-reports'] })
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Fehler'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => sickApi.delete(id),
    onSuccess: () => {
      toast.success('Krankmeldung gelöscht')
      setDeleteModal({ open: false, report: null })
      queryClient.invalidateQueries({ queryKey: ['sick-reports'] })
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Fehler'),
  })

  const handleEndSickReport = () => {
    if (!endModal.report) return
    endMutation.mutate({ id: endModal.report.id, endDate })
  }

  const handleDeleteSickReport = () => {
    if (!deleteModal.report) return
    deleteMutation.mutate(deleteModal.report.id)
  }

  const saving = endMutation.isPending || deleteMutation.isPending

  const columns: ColumnDef<SickReport>[] = [
    {
      accessorKey: 'user_name',
      header: 'Mitarbeiter',
      cell: ({ row }) => <span className="font-medium">{row.original.user_name}</span>,
    },
    {
      accessorKey: 'project_name',
      header: 'Bauvorhaben',
      cell: ({ row }) => row.original.project_name || '-',
    },
    {
      accessorKey: 'start_date',
      header: 'Krank seit',
      cell: ({ row }) => formatDate(row.original.start_date),
    },
    {
      accessorKey: 'end_date',
      header: 'Bis',
      cell: ({ row }) => row.original.end_date ? formatDate(row.original.end_date) : '-',
    },
    {
      accessorKey: 'actual_sick_days',
      header: 'Tage',
      cell: ({ row }) => {
        const r = row.original
        if (r.actual_sick_days != null) {
          return (
            <span>
              {r.actual_sick_days}
              {r.days_count && r.actual_sick_days < r.days_count && (
                <span className="ml-1 text-xs text-muted-foreground">(von {r.days_count})</span>
              )}
            </span>
          )
        }
        return r.days_count || '-'
      },
    },
    {
      id: 'proof',
      header: 'Foto/Grund',
      enableSorting: false,
      cell: ({ row }) => {
        const r = row.original
        if (r.photo_url) {
          return (
            <button
              onClick={(e) => { e.stopPropagation(); setPhotoModal({ open: true, url: r.photo_url }) }}
              className="text-primary-500 hover:text-primary-700"
            >
              <Image className="h-5 w-5" />
            </button>
          )
        }
        if (r.no_proof_reason) {
          return (
            <span className="text-xs text-amber-600" title={r.no_proof_reason}>
              {r.no_proof_reason.length > 20 ? r.no_proof_reason.slice(0, 20) + '...' : r.no_proof_reason}
            </span>
          )
        }
        return '-'
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={row.original.status === 'active' ? 'destructive' : 'secondary'}>
          {getStatusLabel(row.original.status)}
        </Badge>
      ),
    },
    {
      id: 'actions',
      header: 'Aktionen',
      enableSorting: false,
      cell: ({ row }) => {
        const r = row.original
        return (
          <div className="flex gap-2">
            {r.status === 'active' && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  setEndDate(new Date().toISOString().split('T')[0])
                  setEndModal({ open: true, report: r })
                }}
              >
                <CheckCircle className="h-4 w-4" /> Beenden
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={(e) => { e.stopPropagation(); setDeleteModal({ open: true, report: r }) }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )
      },
    },
  ]

  const statCards = [
    { label: 'Aktuell krank', value: stats?.activeCount || 0, icon: ThermometerSun, color: 'text-red-600', bg: 'bg-red-50' },
    { label: 'Kranktage diesen Monat', value: stats?.monthlyDays || 0, icon: Calendar, color: 'text-yellow-600', bg: 'bg-yellow-50' },
    { label: 'Kranktage dieses Jahr', value: stats?.yearlyDays || 0, icon: Calendar, color: 'text-blue-600', bg: 'bg-blue-50' },
  ]

  return (
    <div>
      <PageHeader
        title="Krankmeldungen"
        description="Übersicht aller Krankmeldungen"
        actions={
          <Button variant="outline" className="bg-white" onClick={() => setAddModal(true)}>
            <Plus className="h-4 w-4" /> Krankmeldung eintragen
          </Button>
        }
      />

      <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-3">
        {statCards.map((s, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div><p className="text-3xl font-bold">{s.value}</p><p className="mt-1 text-sm text-muted-foreground">{s.label}</p></div>
                <div className={cn('rounded-lg p-3', s.bg)}><s.icon className={cn('h-6 w-6', s.color)} /></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="mb-6"><CardContent className="flex flex-wrap gap-4 py-4">
        <div className="relative min-w-[200px] max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Suche..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="flex h-9 w-48 rounded-md border border-input bg-white px-3 py-1 text-sm shadow-sm">
          <option value="all">Alle Status</option>
          <option value="active">Aktiv</option>
          <option value="ended">Beendet</option>
        </select>
      </CardContent></Card>

      {isLoading ? (
        <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : (
        <DataTable
          columns={columns}
          data={search ? reports.filter((r: SickReport) => r.user_name?.toLowerCase().includes(search.toLowerCase())) : reports}
          emptyText="Keine Krankmeldungen vorhanden"
        />
      )}

      {/* End Dialog */}
      <Dialog open={endModal.open} onOpenChange={(o) => !o && setEndModal({ open: false, report: null })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Krankmeldung beenden</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Krankmeldung von <strong>{endModal.report?.user_name}</strong> beenden.</p>
          <div className="space-y-2">
            <label className="text-sm font-medium">Enddatum</label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEndModal({ open: false, report: null })}>Abbrechen</Button>
            <Button onClick={handleEndSickReport} disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Beenden</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Photo Dialog */}
      <Dialog open={photoModal.open} onOpenChange={(o) => !o && setPhotoModal({ open: false, url: null })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Krankmeldungs-Foto</DialogTitle></DialogHeader>
          {photoModal.url && <img src={photoModal.url} alt="Krankmeldung" className="w-full rounded-lg" />}
        </DialogContent>
      </Dialog>

      {/* Add Dialog */}
      <Dialog open={addModal} onOpenChange={setAddModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Krankmeldung manuell eintragen</DialogTitle></DialogHeader>
          <AddSickForm
            users={users as UserOption[]}
            onClose={() => setAddModal(false)}
            onSuccess={() => {
              setAddModal(false)
              queryClient.invalidateQueries({ queryKey: ['sick-reports'] })
              toast.success('Krankmeldung eingetragen')
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteModal.open} onOpenChange={(o) => !o && setDeleteModal({ open: false, report: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Krankmeldung löschen</DialogTitle>
            <DialogDescription>Moechten Sie die Krankmeldung von "{deleteModal.report?.user_name}" wirklich löschen?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModal({ open: false, report: null })}>Abbrechen</Button>
            <Button variant="destructive" onClick={handleDeleteSickReport} disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Löschen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
